import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PaperPlaneTilt, Clock, EnvelopeSimple, ChatCircleDots, Car } from "@phosphor-icons/react";

const PRESETS = [5, 10, 15, 30, 60];

export default function ArrivalNotifyDialog({ open, onClose, intervention, chantier, client, onSent }) {
  const [eta, setEta] = useState(15);
  const [customMessage, setCustomMessage] = useState("");
  const [channelEmail, setChannelEmail] = useState(true);
  const [channelSms, setChannelSms] = useState(false);
  // GPS captation is disabled on the web (Karim 2026-02-19). The mobile app
  // still sends its own position when the tech notifies from the phone.
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      setEta(15);
      setCustomMessage("");
      setChannelEmail(!!(client?.email));
      setChannelSms(!!(client?.phone));
      // Web browser GPS captation is disabled — the arrival notification is
      // sent without GPS coordinates. The mobile app still attaches its own
      // GPS server-side when the tech notifies from the phone.
    }
  }, [open, client]);

  const now = new Date();
  const etaDate = new Date(now.getTime() + eta * 60000);
  const etaLabel = etaDate.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

  const send = async () => {
    if (!channelEmail && !channelSms) return toast.error("Choisissez au moins un canal");
    if (channelEmail && !client?.email) return toast.error("Aucune adresse email pour ce client");
    setSending(true);
    try {
      const payload = {
        eta_minutes: eta,
        channel_email: channelEmail,
        channel_sms: channelSms,
        custom_message: customMessage || null,
      };
      const r = await api.post(`/interventions/${intervention.id}/notify-arrival`, payload);
      const parts = [];
      if (r.data.email_sent) parts.push("email");
      if (r.data.sms_sent) parts.push("SMS");
      toast.success(`Client prévenu ${parts.length ? "par " + parts.join(" + ") : ""} — arrivée ${r.data.eta_time}`);
      onSent?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md bg-[#111] border-white/10 text-white rounded-2xl" data-testid="arrival-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Car size={20} className="text-emerald-400" weight="duotone" /> Je suis en route
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-2xl bg-gradient-to-br from-emerald-500/15 to-emerald-500/5 border border-emerald-500/30 p-4 text-center">
            <div className="text-[10px] uppercase tracking-widest text-emerald-300">Arrivée estimée</div>
            <div className="font-display font-bold text-4xl mt-1 text-emerald-400">{etaLabel}</div>
            <div className="text-xs text-white/60 mt-1">dans {eta} minute{eta > 1 ? "s" : ""}</div>
          </div>

          <div>
            <Label className="text-white/60 text-[11px] uppercase tracking-widest flex items-center gap-1.5 mb-2">
              <Clock size={12} /> Délai
            </Label>
            <div className="flex gap-1.5 flex-wrap">
              {PRESETS.map((m) => (
                <button
                  key={m}
                  onClick={() => setEta(m)}
                  className={`text-xs px-3 py-2 rounded-full border transition-colors ${
                    eta === m ? "bg-emerald-500 border-emerald-500 text-white" : "bg-white/5 border-white/10 text-white/70"
                  }`}
                  data-testid={`eta-${m}`}
                >
                  {m < 60 ? `${m} min` : `${m / 60}h`}
                </button>
              ))}
              <Input
                type="number"
                min="1"
                max="240"
                value={eta}
                onChange={(e) => setEta(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-20 h-9 bg-white/5 border-white/10 text-center text-sm"
                data-testid="eta-custom"
              />
            </div>
          </div>

          <div>
            <Label className="text-white/60 text-[11px] uppercase tracking-widest mb-2 block">Canaux</Label>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between rounded-xl bg-white/5 border border-white/10 p-3">
                <div className="flex items-center gap-2">
                  <EnvelopeSimple size={16} className="text-[#005CFF]" weight="duotone" />
                  <div>
                    <div className="text-sm">Email</div>
                    <div className="text-[10px] text-white/50">{client?.email || <span className="text-amber-400">Pas d&apos;email client</span>}</div>
                  </div>
                </div>
                <Switch checked={channelEmail} onCheckedChange={setChannelEmail} disabled={!client?.email} className="data-[state=checked]:bg-[#005CFF]" data-testid="channel-email" />
              </div>
              <div className="flex items-center justify-between rounded-xl bg-white/5 border border-white/10 p-3">
                <div className="flex items-center gap-2">
                  <ChatCircleDots size={16} className="text-emerald-400" weight="duotone" />
                  <div>
                    <div className="text-sm">SMS</div>
                    <div className="text-[10px] text-white/50">{client?.phone || <span className="text-amber-400">Pas de tél. client</span>} · Twilio requis</div>
                  </div>
                </div>
                <Switch checked={channelSms} onCheckedChange={setChannelSms} disabled={!client?.phone} className="data-[state=checked]:bg-emerald-500" data-testid="channel-sms" />
              </div>
            </div>
          </div>

          <div>
            <Label className="text-white/60 text-[11px] uppercase tracking-widest">Message personnalisé (optionnel)</Label>
            <Textarea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              rows={2}
              placeholder="Laisser vide pour utiliser le modèle par défaut"
              className="bg-white/5 border-white/10 mt-1 text-sm"
              data-testid="arrival-custom-msg"
            />
          </div>

          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} className="flex-1 border border-white/10">Annuler</Button>
            <Button onClick={send} disabled={sending} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="arrival-send-btn">
              <PaperPlaneTilt size={14} weight="bold" /> {sending ? "Envoi…" : "Prévenir le client"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
